import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink, Monitor, Smartphone, Tablet, Globe, Clock, MousePointer, Filter, X, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';
import * as api from '../services/api';

const StarDisplay = ({ rating }) => {
  if (!rating) return <span className="text-slate-300 text-xs">Sin calificar</span>;
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <span key={i} className={`text-sm ${i <= rating ? 'text-amber-400' : 'text-slate-200'}`}>&#9733;</span>
      ))}
    </div>
  );
};

const DeviceIcon = ({ type }) => {
  if (!type) return <Globe className="w-3.5 h-3.5 text-slate-400" />;
  const t = type.toLowerCase();
  if (t.includes('mobile')) return <Smartphone className="w-3.5 h-3.5 text-blue-500" />;
  if (t.includes('tablet')) return <Tablet className="w-3.5 h-3.5 text-purple-500" />;
  return <Monitor className="w-3.5 h-3.5 text-slate-500" />;
};

const TraffickerPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [filterSource, setFilterSource] = useState('');
  const [filterMedium, setFilterMedium] = useState('');
  const [pipelines, setPipelines] = useState([]);
  const [filterPipeline, setFilterPipeline] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [leadsRes, pipelinesRes] = await Promise.all([
          api.getTraffickerLeads(),
          api.getPipelines()
        ]);
        setLeads(leadsRes || []);
        setPipelines(pipelinesRes || []);
      } catch (err) {
        console.error('Error loading trafficker data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Extract unique sources and mediums for filters
  const { sources, mediums } = useMemo(() => {
    const s = new Set(), m = new Set();
    leads.forEach(l => {
      const td = l.trackingData || {};
      if (td.utm_source) s.add(td.utm_source);
      if (td.utm_medium) m.add(td.utm_medium);
      if (l.source) s.add(l.source);
    });
    return { sources: [...s].sort(), mediums: [...m].sort() };
  }, [leads]);

  // Filter and sort
  const filtered = useMemo(() => {
    let result = leads.filter(l => {
      const td = l.trackingData || {};
      const searchLower = search.toLowerCase();
      const matchSearch = !search || 
        (l.name || '').toLowerCase().includes(searchLower) ||
        (l.email || '').toLowerCase().includes(searchLower) ||
        (td.utm_source || '').toLowerCase().includes(searchLower) ||
        (td.utm_campaign || '').toLowerCase().includes(searchLower) ||
        (td.channel || '').toLowerCase().includes(searchLower) ||
        (l.source || '').toLowerCase().includes(searchLower);
      
      const matchSource = !filterSource || 
        td.utm_source === filterSource || l.source === filterSource;
      const matchMedium = !filterMedium || td.utm_medium === filterMedium;
      const matchPipeline = !filterPipeline || l.pipelineId === filterPipeline;
      
      return matchSearch && matchSource && matchMedium && matchPipeline;
    });

    result.sort((a, b) => {
      let va, vb;
      if (sortField === 'name' || sortField === 'email' || sortField === 'source') {
        va = (a[sortField] || '').toLowerCase();
        vb = (b[sortField] || '').toLowerCase();
      } else if (sortField === 'rating') {
        va = a.rating || 0;
        vb = b.rating || 0;
      } else if (sortField === 'utm_source' || sortField === 'utm_medium' || sortField === 'utm_campaign') {
        va = ((a.trackingData || {})[sortField] || '').toLowerCase();
        vb = ((b.trackingData || {})[sortField] || '').toLowerCase();
      } else {
        va = a[sortField] || '';
        vb = b[sortField] || '';
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [leads, search, filterSource, filterMedium, filterPipeline, sortField, sortDir]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [search, filterSource, filterMedium, filterPipeline]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginatedLeads = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-slate-300" />;
    return sortDir === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-indigo-500" /> 
      : <ArrowDown className="w-3 h-3 text-indigo-500" />;
  };

  const hasActiveFilters = filterSource || filterMedium || filterPipeline;

  const exportToExcel = () => {
    const rows = filtered.map(l => {
      const td = l.trackingData || {};
      return {
        'Codigo': l.code || '',
        'Nombre': l.name || '',
        'Correo': l.email || '',
        'Telefono': l.phone || '',
        'Calificacion': l.rating || '',
        'Canal': td.channel || l.source || '',
        'UTM Source': td.utm_source || '',
        'UTM Medium': td.utm_medium || '',
        'UTM Campaign': td.utm_campaign || '',
        'UTM Content': td.utm_content || '',
        'UTM Term': td.utm_term || '',
        'Dispositivo': td.device_type || '',
        'IP': td.ip_address || '',
        'Paginas Visitadas': td.pages_visited || td.pagesVisited || '',
        'Tiempo Llenado (s)': td.form_fill_duration ? Math.round(td.form_fill_duration) : '',
        'URL Conversion': td.conversion_url || '',
        'Referrer URL': td.referrer_url || '',
        'User Agent': td.user_agent || '',
        'Fecha': l.createdAt ? new Date(l.createdAt).toLocaleDateString('es-CO') : '',
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trafficker');
    XLSX.writeFile(wb, `trafficker_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-full" data-testid="trafficker-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Trafficker</h1>
        <p className="text-sm text-slate-500 mt-1">Datos de tracking y origen de todos los leads</p>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email, fuente, campana..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="trafficker-search"
            />
          </div>
          
          <select
            value={filterPipeline}
            onChange={e => setFilterPipeline(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="trafficker-filter-pipeline"
          >
            <option value="">Todos los embudos</option>
            {pipelines.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <select
            value={filterSource}
            onChange={e => setFilterSource(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="trafficker-filter-source"
          >
            <option value="">Todas las fuentes</option>
            {sources.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select
            value={filterMedium}
            onChange={e => setFilterMedium(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="trafficker-filter-medium"
          >
            <option value="">Todos los medios</option>
            {mediums.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={() => { setFilterSource(''); setFilterMedium(''); setFilterPipeline(''); }}
              className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              data-testid="trafficker-clear-filters"
            >
              <X className="w-3.5 h-3.5" /> Limpiar
            </button>
          )}

          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-slate-500">
              {filtered.length} de {leads.length} leads
            </span>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
              data-testid="trafficker-export-excel"
            >
              <Download className="w-4 h-4" /> Exportar Excel
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-auto max-h-[65vh]">
          <table className="w-full text-sm" data-testid="trafficker-table">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200">
                {[
                  { field: 'name', label: 'Lead' },
                  { field: 'email', label: 'Correo' },
                  { field: 'rating', label: 'Calif.' },
                  { field: 'source', label: 'Canal' },
                  { field: 'utm_source', label: 'UTM Source' },
                  { field: 'utm_medium', label: 'UTM Medium' },
                  { field: 'utm_campaign', label: 'UTM Campaign' },
                  { field: null, label: 'Dispositivo' },
                  { field: null, label: 'IP' },
                  { field: null, label: 'Paginas' },
                  { field: null, label: 'Tiempo' },
                  { field: 'createdAt', label: 'Fecha' },
                ].map(col => (
                  <th
                    key={col.label}
                    className={`px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap bg-slate-50 ${col.field ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                    onClick={col.field ? () => handleSort(col.field) : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {col.field && <SortIcon field={col.field} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedLeads.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-6 py-12 text-center text-slate-400">
                    No se encontraron leads con datos de tracking
                  </td>
                </tr>
              ) : (
                paginatedLeads.map(lead => {
                  const td = lead.trackingData || {};
                  const duration = td.form_fill_duration;
                  const durationStr = duration ? `${Math.round(duration)}s` : '-';
                  const pages = td.pages_visited || td.pagesVisited;
                  const pagesStr = pages ? String(pages) : '-';
                  
                  return (
                    <tr 
                      key={lead.id} 
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/lead/${lead.id}`)}
                      data-testid={`trafficker-row-${lead.id}`}
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-indigo-500 font-mono">{lead.code || '-'}</span>
                          <span className="font-medium text-slate-900 truncate max-w-[160px]">{lead.name || 'Sin nombre'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-600 truncate max-w-[180px]">{lead.email || '-'}</td>
                      <td className="px-3 py-3"><StarDisplay rating={lead.rating} /></td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 truncate max-w-[120px]">
                          {td.channel || lead.source || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-slate-600 font-mono text-xs">{td.utm_source || '-'}</td>
                      <td className="px-3 py-3 text-slate-600 font-mono text-xs">{td.utm_medium || '-'}</td>
                      <td className="px-3 py-3 text-slate-600 font-mono text-xs truncate max-w-[150px]">{td.utm_campaign || '-'}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <DeviceIcon type={td.device_type} />
                          <span className="text-xs text-slate-500 capitalize">{td.device_type || '-'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-500 font-mono text-xs">{td.ip_address || '-'}</td>
                      <td className="px-3 py-3 text-center">
                        {pages ? (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                            <MousePointer className="w-3 h-3" /> {pagesStr}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {duration ? (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                            <Clock className="w-3 h-3" /> {durationStr}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>Mostrando {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filtered.length)} de {filtered.length}</span>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                className="px-2 py-1 border border-slate-200 rounded text-xs">
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs">por pagina</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="p-1.5 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let page;
                if (totalPages <= 7) page = i + 1;
                else if (currentPage <= 4) page = i + 1;
                else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
                else page = currentPage - 3 + i;
                return (
                  <button key={page} onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded text-xs font-medium ${currentPage === page ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-200'}`}>
                    {page}
                  </button>
                );
              })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="p-1.5 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TraffickerPage;
